import { useState } from 'react';
import useTranslation from '~/client/hooks/i18n/useTranslation';
import useCurrentUser from '~/client/hooks/user/useCurrentUser';
import capitalize from '~/shared/utils/string/capitalize';
import useClaimPrize from './useClaimPrize';
import useOptin from './useOptin';
import type { TournamentFragment } from '../../../graphql/TournamentFragment.generated';

type Props = {
  tournament?: TournamentFragment;
  page?: 'list' | 'details' | 'game';
  name?: string;
};

const useTournamentActionButton = ({ tournament, page, name }: Props) => {
  const [showDialog, setShowDialog] = useState(false);
  const toggleDialog = () => setShowDialog(!showDialog);
  const { user } = useCurrentUser();
  const { id = '', iAmOptedIn = false, myScore } = tournament || {};
  const DefaultOptions = {
    optedIn: 'general.button.participating',
    default: 'general.promotion.optin',
  };

  const PastOptions = {
    claim: 'game-leaderboard.claim-prize',
    default: 'gameleaderboard.past',
  };
  const { handleOptIn, loading: loadingOptin } = useOptin({
    tournamentId: id,
    page,
    handle: name,
  });
  const {
    handleClaim,
    loading: loadingClaim,
    isAutomaticallyReleased,
  } = useClaimPrize({
    scoreId: myScore?.id ?? '',
    toggleDialog,
    tournament,
  });
  const loginTranslation = useTranslation('profile.login');

  const pastTranslation = capitalize(
    useTranslation(
      PastOptions[myScore?.canClaimPrize ? 'claim' : 'default'],
    ).toLowerCase(),
  );
  const defaultTranslation = capitalize(
    useTranslation(
      DefaultOptions[iAmOptedIn ? 'optedIn' : 'default'],
    ).toLowerCase(),
  );

  const handleAction = !iAmOptedIn ? handleOptIn : handleClaim;

  const canClaimPrize = tournament?.myScore?.canClaimPrize;

  const status = tournament?.activityType;

  const isDisabled =
    !!user?.id &&
    ((status !== 'past' && iAmOptedIn) ||
      (status === 'past' && !canClaimPrize));

  return {
    handleAction,
    toggleDialog,
    showDialog,
    loading: loadingOptin || loadingClaim,
    pastTranslation: !user?.id ? loginTranslation : pastTranslation,
    defaultTranslation: !user?.id ? loginTranslation : defaultTranslation,
    canClaimPrize,
    isAutomaticallyReleased,
    isDisabled,
    status,
  };
};

export default useTournamentActionButton;
